import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Users, DollarSign, CalendarDays, ListPlus, Search, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const openWa = (phone, text) => {
  const digits = (phone || "").replace(/[^\d]/g, "");
  if (!digits) return;
  window.open(`https://wa.me/${digits}?text=${encodeURIComponent(text)}`, "_blank");
};

const reminderMsg = (m, reg) =>
  `Hi ${reg.parent_name || "there"}, a quick reminder from SCC Football Academy: payment` +
  `${reg.outstanding ? ` of $${reg.outstanding}` : ""} for ${m.name}'s ${reg.term_name || "term"} is still outstanding. ` +
  `Please complete payment to confirm ${m.name}'s place. Thank you! ⚽`;

const DayPills = ({ days }) => (
  <span className="flex flex-wrap gap-1">
    {(days || []).map((d) => (
      <span key={d} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-700/70 text-slate-200">{d}</span>
    ))}
  </span>
);

function BulkAssignDialog({ programmes, onClose, onApplied }) {
  const [termId, setTermId] = useState("");
  const [sessions, setSessions] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [members, setMembers] = useState([]);
  const [checked, setChecked] = useState({});
  const [original, setOriginal] = useState({});
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!termId) { setSessions([]); setTemplateId(""); return; }
    axios.get(`${API}/academy/holding-group/term-sessions?term_id=${termId}`)
      .then((r) => setSessions(r.data.sessions || [])).catch(() => setSessions([]));
  }, [termId]);

  useEffect(() => {
    if (!templateId || !termId) return;
    // Members = the bucket rosters for the selected programme's period (use all buckets)
    Promise.all([
      axios.get(`${API}/academy/holding-group?term_id=${termId}`),
      axios.get(`${API}/academy/holding-group/session-roster?template_id=${templateId}`),
    ]).then(([hg, roster]) => {
      const all = [];
      (hg.data.buckets || []).forEach((b) => (b.members || []).forEach((m) => all.push({ ...m, bucket: b.name })));
      const enrolled = {};
      (roster.data.player_ids || []).forEach((id) => { enrolled[id] = true; });
      setMembers(all);
      setChecked({ ...enrolled });
      setOriginal({ ...enrolled });
    });
  }, [templateId, termId]);

  const filtered = useMemo(
    () => members.filter((m) => m.name.toLowerCase().includes(q.toLowerCase())),
    [members, q]
  );

  const apply = async () => {
    const add = [], remove = [];
    const ids = new Set(members.map((m) => m.player_id));
    ids.forEach((id) => {
      const was = !!original[id], now = !!checked[id];
      if (now && !was) add.push(id);
      if (!now && was) remove.push(id);
    });
    if (!add.length && !remove.length) { toast.info("No changes"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/academy/holding-group/bulk-roster`, { template_id: templateId, add, remove });
      toast.success(`Updated roster — added ${add.length}, removed ${remove.length}`);
      onApplied();
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-slate-900 border-slate-700 text-slate-100">
        <DialogHeader><DialogTitle>Bulk assign to a session</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-slate-400">Programme (term)</label>
            <Select value={termId} onValueChange={setTermId}>
              <SelectTrigger data-testid="bulk-term-select" className="bg-slate-800 border-slate-700"><SelectValue placeholder="Choose programme" /></SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700 text-slate-100">
                {programmes.map((p) => <SelectItem key={p.term_id} value={p.term_id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-400">Weekday session</label>
            <Select value={templateId} onValueChange={setTemplateId} disabled={!sessions.length}>
              <SelectTrigger data-testid="bulk-session-select" className="bg-slate-800 border-slate-700"><SelectValue placeholder={sessions.length ? "Choose session" : "No sessions"} /></SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700 text-slate-100">
                {sessions.map((s) => <SelectItem key={s.template_id} value={s.template_id}>{s.label} ({s.enrolled_count})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {templateId && (
          <>
            <div className="relative mt-2">
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-500" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search players…" className="pl-8 bg-slate-800 border-slate-700" data-testid="bulk-search" />
            </div>
            <div className="max-h-72 overflow-y-auto mt-2 border border-slate-800 rounded divide-y divide-slate-800">
              {filtered.map((m) => (
                <label key={m.player_id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-800/50 cursor-pointer">
                  <input type="checkbox" checked={!!checked[m.player_id]}
                    onChange={(e) => setChecked((c) => ({ ...c, [m.player_id]: e.target.checked }))}
                    data-testid={`bulk-check-${m.player_id}`} />
                  <span className="flex-1 text-sm">{m.name}</span>
                  <span className="text-xs text-slate-400">{m.age_group} · {m.bucket}</span>
                  {m.paid && <DollarSign className="w-3.5 h-3.5 text-emerald-400" />}
                </label>
              ))}
              {!filtered.length && <p className="text-sm text-slate-500 p-3">No players.</p>}
            </div>
          </>
        )}

        <DialogFooter>
          <Button variant="outline" className="border-slate-600" onClick={onClose}>Cancel</Button>
          <Button onClick={apply} disabled={!templateId || saving} data-testid="bulk-apply" className="bg-emerald-600 hover:bg-emerald-500">{saving ? "Saving…" : "Apply changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AcademyHoldingGroup() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("");
  const [termId, setTermId] = useState("");
  const [showBulk, setShowBulk] = useState(false);
  const [unpaidOnly, setUnpaidOnly] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    const params = [];
    if (termId) params.push(`term_id=${termId}`);
    else if (period) params.push(`period=${encodeURIComponent(period)}`);
    axios.get(`${API}/academy/holding-group${params.length ? "?" + params.join("&") : ""}`)
      .then((r) => setData(r.data)).catch(() => toast.error("Failed to load"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [period, termId]); // eslint-disable-line

  const programmesForPeriod = useMemo(() => {
    const all = data?.filters?.programmes || [];
    return period ? all.filter((p) => p.period === period) : all;
  }, [data, period]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4 sm:p-6">
      <div className="max-w-5xl mx-auto">
        <Link to="/admin" className="inline-flex items-center text-slate-400 hover:text-white text-sm mb-4" data-testid="hg-back"><ArrowLeft className="w-4 h-4 mr-1" />Back to Admin</Link>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
          <h1 className="text-2xl font-bold flex items-center gap-2"><Users className="w-6 h-6 text-blue-400" />Holding Group</h1>
          <Button onClick={() => setShowBulk(true)} className="bg-blue-600 hover:bg-blue-500" data-testid="open-bulk-assign"><ListPlus className="w-4 h-4 mr-1" />Bulk assign to session</Button>
        </div>
        <p className="text-sm text-slate-400 mb-4">Age-group buckets stay live across terms. Filter by season/programme to see who registered. <span className="text-emerald-400 font-medium"><DollarSign className="w-3 h-3 inline" />= paid in full</span>, no sign = payment outstanding.</p>

        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3 mb-5 p-3 bg-slate-900/60 border border-slate-800 rounded-lg">
          <div className="min-w-[180px]">
            <label className="text-xs text-slate-400 flex items-center gap-1"><CalendarDays className="w-3 h-3" />Season / period</label>
            <Select value={period || "all"} onValueChange={(v) => { setPeriod(v === "all" ? "" : v); setTermId(""); }}>
              <SelectTrigger data-testid="period-select" className="bg-slate-800 border-slate-700"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700 text-slate-100">
                <SelectItem value="all">All periods</SelectItem>
                {(data?.filters?.periods || []).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[220px]">
            <label className="text-xs text-slate-400">Programme</label>
            <Select value={termId || "all"} onValueChange={(v) => setTermId(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="programme-select" className="bg-slate-800 border-slate-700"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700 text-slate-100">
                <SelectItem value="all">All programmes</SelectItem>
                {programmesForPeriod.map((p) => <SelectItem key={p.term_id} value={p.term_id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {(period || termId) && <Button variant="ghost" className="text-slate-400" onClick={() => { setPeriod(""); setTermId(""); }} data-testid="clear-filters">Clear</Button>}
          <Button variant={unpaidOnly ? "default" : "outline"} className={unpaidOnly ? "bg-red-600 hover:bg-red-500" : "border-slate-600 text-slate-300"} onClick={() => setUnpaidOnly((v) => !v)} data-testid="unpaid-only-toggle">
            {unpaidOnly ? "Showing unpaid only" : "Unpaid only"}
          </Button>
        </div>

        {loading && <p className="text-slate-500">Loading…</p>}
        {!loading && data?.buckets?.map((b) => {
          const rows = unpaidOnly ? b.members.filter((m) => m.registered && !m.paid) : b.members;
          if (unpaidOnly && !rows.length) return null;
          return (
          <Card key={b.team_id} className="mb-4 bg-slate-900/60 border-slate-800 p-4" data-testid={`bucket-${b.team_id}`}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-lg">{b.name}</h3>
              <div className="flex gap-2 text-xs">
                <Badge className="bg-slate-700 text-slate-200">{b.member_count} in bucket</Badge>
                {(period || termId) && <Badge className="bg-blue-600/30 text-blue-200 border-blue-600/40">{b.registered_count} registered</Badge>}
                <Badge className="bg-emerald-600/30 text-emerald-200 border-emerald-600/40">{b.paid_count} paid</Badge>
                {unpaidOnly && <Badge className="bg-red-600/30 text-red-200 border-red-600/40">{rows.length} unpaid</Badge>}
              </div>
            </div>
            <div className="divide-y divide-slate-800">
              {rows.map((m) => {
                const unpaidReg = (m.registrations || []).find((r) => !r.paid) || (m.all_registrations || []).find((r) => !r.paid);
                return (
                <div key={m.player_id} className="flex items-center gap-3 py-2" data-testid={`member-${m.player_id}`}>
                  <span className="flex-1 flex items-center gap-2 text-sm">
                    {m.name}
                    {m.played_up && <span className="text-[10px] px-1 rounded bg-amber-500/20 text-amber-300">played up</span>}
                  </span>
                  <span className="text-xs text-slate-500 w-12">{m.age_group}</span>
                  {unpaidOnly ? (
                    <span className="flex items-center gap-2">
                      {unpaidReg?.outstanding ? <span className="text-xs text-red-300">${unpaidReg.outstanding} due</span> : null}
                      <DayPills days={m.days} />
                      <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-500"
                        disabled={!unpaidReg?.parent_phone}
                        onClick={() => openWa(unpaidReg.parent_phone, reminderMsg(m, unpaidReg))}
                        data-testid={`chase-wa-${m.player_id}`}>
                        <MessageCircle className="w-3 h-3 mr-1" />Remind
                      </Button>
                    </span>
                  ) : (period || termId) ? (
                    m.registered ? (
                      <span className="flex items-center gap-2">
                        <DayPills days={m.days} />
                        {m.paid
                          ? <Badge className="bg-emerald-600/30 text-emerald-200 border-emerald-600/40" data-testid={`paid-${m.player_id}`}><DollarSign className="w-3 h-3 mr-0.5" />Paid</Badge>
                          : <Badge className="bg-red-600/25 text-red-300 border-red-600/40" data-testid={`unpaid-${m.player_id}`}>Unpaid</Badge>}
                      </span>
                    ) : <span className="text-xs text-slate-600">— not registered</span>
                  ) : (
                    <span className="flex flex-wrap gap-1 justify-end max-w-[50%]">
                      {m.all_registrations?.length
                        ? m.all_registrations.map((r, i) => (
                            <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded border ${r.paid ? "bg-emerald-600/20 text-emerald-200 border-emerald-600/40" : "bg-red-600/15 text-red-300 border-red-600/40"}`}>
                              {r.period}{r.paid ? " $" : ""}
                            </span>
                          ))
                        : <span className="text-xs text-slate-600">no registrations</span>}
                    </span>
                  )}
                </div>
                );
              })}
              {!rows.length && <p className="text-sm text-slate-600 py-2">No players in this bucket.</p>}
            </div>
          </Card>
          );
        })}
        {!loading && !data?.buckets?.length && <p className="text-slate-500">No buckets configured. Flag a team as a bucket in Teams Management.</p>}
      </div>

      {showBulk && <BulkAssignDialog programmes={data?.filters?.programmes || []} onClose={() => setShowBulk(false)} onApplied={load} />}
    </div>
  );
}
